import { Hammer, PenLine } from 'lucide-react'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'

import { FormModal } from '@components/branded-modal'
import { ProductForm } from '@components/product'
import { StatusInfo } from '@components/status-info'
import { AdasBox, AdasButton, AdasCard, AdasTypography } from '@components/wrapper-components'
import { useUserStore } from '../../../store'
import { currencyFormatter } from '../../../utils/utils'

export const ProductInfo = ({ open = true, product, productTypeName, closeDrawer, onSaveSuccess, selectedProfileType }) => {
  const [updatedProductInfo, setUpdatedProductInfo] = useState(product)
  const user = useUserStore((store) => store.currentUser)

  const isTechUser = useMemo(() => {
    if (user) {
      return !(user.is_admin || user.is_manager || user.is_owner)
    }
    return false
  }, [user])

  // Open the edit form directly when the row is clicked, unless the user is read-only
  const canEdit = !isTechUser && selectedProfileType !== 'national'
  const [isProductEdit, setIsProductEdit] = useState(canEdit)

  const onSaveProductSuccessHandler = (props) => {
    setUpdatedProductInfo(props)
    onSaveSuccess()
    setIsProductEdit(false)
    closeDrawer()
  }

  const priceLabel = productTypeName === 'labor' ? 'Hourly Rate' : 'Price'
  const quantityLabel = productTypeName === 'labor' ? 'Hours' : 'Quantity'

  if (isProductEdit) {
    return (
      <ProductForm
        open={open}
        productData={updatedProductInfo}
        close={closeDrawer}
        onSaveSuccess={onSaveProductSuccessHandler}
        productTypeName={productTypeName}
        edit={true}
        selectedProfileType={selectedProfileType}
      />
    )
  }

  const footer = canEdit ? (
    <div className='flex items-center justify-end'>
      <AdasButton startIcon={<PenLine />} aria-label='Edit' color='primary' onClick={() => setIsProductEdit(true)}>
        Edit
      </AdasButton>
    </div>
  ) : null

  return (
    <FormModal
      open={open}
      onClose={closeDrawer}
      title={<span className='capitalize'>{productTypeName}</span>}
      icon={Hammer}
      footer={footer}
    >
      <AdasBox>
        <AdasCard className='adas-card'>
          <AdasTypography component='span'>
            <AdasTypography variant='overline'>
              {productTypeName} ID #{product.itemNum}
            </AdasTypography>
            <StatusInfo
              color={updatedProductInfo.is_active ? 'primary' : 'default'}
              label={updatedProductInfo?.is_active ? 'Active' : 'Inactive'}
              className='float-right'
            />
          </AdasTypography>
          <AdasTypography className='font-semibold mt-4' variant='h6'>
            {updatedProductInfo.name}
          </AdasTypography>
          <AdasTypography>{updatedProductInfo.type}</AdasTypography>
          {updatedProductInfo.category && [
            <AdasTypography key={updatedProductInfo.category + '_label'} className='mt-2 font-semibold' variant='subtitle2'>
              Category
            </AdasTypography>,
            <AdasTypography key={updatedProductInfo.category}>{updatedProductInfo?.category?.name}</AdasTypography>
          ]}
          <AdasTypography className='mt-2 font-semibold' variant='subtitle2'>
            {priceLabel}
          </AdasTypography>
          <AdasTypography>{currencyFormatter(updatedProductInfo.price)}</AdasTypography>

          <AdasTypography className='mt-2 font-semibold' variant='subtitle2'>
            {quantityLabel}
          </AdasTypography>
          <AdasTypography>{updatedProductInfo?.quantity || 'N/A'}</AdasTypography>
          <AdasTypography className='mt-2 font-semibold' variant='subtitle2'>
            Description
          </AdasTypography>
          <AdasTypography className='mb-0'>{updatedProductInfo.description}</AdasTypography>
        </AdasCard>
      </AdasBox>
    </FormModal>
  )
}

ProductInfo.propTypes = {
  open: PropTypes.bool,
  product: PropTypes.object.isRequired,
  productTypeName: PropTypes.string.isRequired,
  closeDrawer: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  selectedProfileType: PropTypes.string
}
